import { useCallback } from "react";
import {
  apiLogin,
  apiVerifyTwoFactor,
  apiRegister,
  apiGetMe,
  apiGetOrders,
  apiGetAddresses,
  apiAddAddress,
  apiUpdateAddress,
  apiDeleteAddress,
  apiSetDefaultAddress,
  apiUpdateProfile,
  apiChangePassword,
  apiForgotPassword,
  apiResendVerification,
  apiGetMyReturns,
  apiLogout,
  apiGoogleLogin,
} from "../../api";
import logger from "../../utils/logger";
import { indexReturnsByItem, mapOrders } from "./authMappers";

export const useAuthActions = ({
  setUser,
  setIsAuthenticated,
  setOrders,
  setAddresses,
}) => {
  const requireCustomerUser = async (data) => {
    if (!data?.user || data.user.role !== "user") {
      localStorage.removeItem("bookscape_token");
      await apiLogout().catch(() => {});
      throw new Error("Admin accounts must sign in through the Admin portal.");
    }
    return data.user;
  };

  const loadUserData = async () => {
    try {
      const [ordersRes, addressRes, returnsRes] = await Promise.all([
        apiGetOrders().catch(() => ({ orders: [] })),
        apiGetAddresses().catch(() => ({ addresses: [] })),
        apiGetMyReturns().catch(() => ({ returns: [] })),
      ]);
      const returnsByItem = indexReturnsByItem(returnsRes.returns || []);
      setOrders(mapOrders(ordersRes.orders || [], returnsByItem));
      setAddresses(addressRes.addresses || []);
    } catch (e) {
      logger.error("Failed to load user data", e);
    }
  };

  const login = async (email, password) => {
    try {
      const data = await apiLogin(email, password);
      if (data.requiresTwoFactor) return { ...data, success: false };
      await requireCustomerUser(data);
      localStorage.setItem("bookscape_token", data.token);
      setUser(data.user);
      setIsAuthenticated(true);
      window.dispatchEvent(
        new CustomEvent("bookscape:auth-changed", {
          detail: { authenticated: true },
        }),
      );
      await loadUserData();
      return { success: true, message: "Login successful!" };
    } catch (error) {
      return {
        success: false,
        message: error.message,
        code: error.data?.code,
        email: error.data?.email,
      };
    }
  };

  const signup = async (name, email, password) => {
    try {
      const data = await apiRegister(name, email, password, "");
      if (data.requiresEmailVerification) {
        return {
          success: true,
          requiresEmailVerification: true,
          email: data.email || email,
          message:
            data.message || "Please check your inbox to verify your email.",
        };
      }
      if (data.token) {
        await requireCustomerUser(data);
        localStorage.setItem("bookscape_token", data.token);
        setUser(data.user);
        setIsAuthenticated(true);
        window.dispatchEvent(
          new CustomEvent("bookscape:auth-changed", {
            detail: { authenticated: true },
          }),
        );
        setOrders([]);
        setAddresses([]);
      }
      return {
        success: true,
        message: data.message || "Account created successfully!",
      };
    } catch (error) {
      return {
        success: false,
        message: error.data?.errors?.join(", ") || error.message,
      };
    }
  };

  const resendVerification = async (email) => {
    try {
      const data = await apiResendVerification(email);
      return {
        success: true,
        message: data.message || "Verification email sent.",
      };
    } catch (error) {
      return {
        success: false,
        message: error.data?.errors?.join(", ") || error.message,
      };
    }
  };

  const logout = () => {
    apiLogout().catch(() => {}); // Best effort — clear cookie server-side
    setUser(null);
    setIsAuthenticated(false);
    setOrders([]);
    setAddresses([]);
    localStorage.removeItem("bookscape_token");
    window.dispatchEvent(
      new CustomEvent("bookscape:auth-changed", {
        detail: { authenticated: false },
      }),
    );
  };

  const resetPassword = async (email) => {
    try {
      const data = await apiForgotPassword(email);
      return {
        success: true,
        message: data.message || "Password reset email sent",
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const updateProfile = async (updates) => {
    try {
      const data = await apiUpdateProfile(updates);
      setUser(data.user);
      return {
        success: true,
        message: data.message || "Profile updated successfully!",
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const data = await apiChangePassword(currentPassword, newPassword);
      updateSecuritySession(data);
      return {
        success: true,
        message: data.message || "Password changed successfully!",
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const addAddress = async (address) => {
    try {
      const data = await apiAddAddress(address);
      setAddresses((prev) => [...prev, data.address]);
      return {
        success: true,
        message: "Address added successfully!",
        address: data.address,
      };
    } catch (error) {
      return {
        success: false,
        message: error.data?.errors?.join(", ") || error.message,
      };
    }
  };

  const updateAddress = async (id, updates) => {
    try {
      const data = await apiUpdateAddress(id, updates);
      setAddresses((prev) =>
        prev.map((a) =>
          a.id === id
            ? data.address
            : updates.isDefault
              ? { ...a, isDefault: false }
              : a,
        ),
      );
      return {
        success: true,
        message: data.message || "Address updated successfully!",
      };
    } catch (error) {
      return {
        success: false,
        message: error.data?.errors?.join(", ") || error.message,
      };
    }
  };

  const deleteAddress = async (id) => {
    try {
      await apiDeleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      return { success: true, message: "Address deleted." };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const setDefaultAddress = async (id) => {
    try {
      await apiSetDefaultAddress(id);
      setAddresses((prev) =>
        prev.map((a) => ({ ...a, isDefault: a.id === id })),
      );
    } catch (error) {
      logger.error("Failed to set default address", error);
    }
  };

  const refreshOrders = useCallback(async () => {
    try {
      const [data, returnsRes] = await Promise.all([
        apiGetOrders(),
        apiGetMyReturns().catch(() => ({ returns: [] })),
      ]);
      const returnsByItem = indexReturnsByItem(returnsRes.returns || []);
      setOrders(mapOrders(data.orders || [], returnsByItem));
    } catch (e) {
      logger.error("Failed to refresh orders", e);
    }
  }, [setOrders]);

  const initFromToken = async () => {
    const data = await apiGetMe();
    await requireCustomerUser(data);
    setUser(data.user);
    setIsAuthenticated(true);
    await loadUserData();
  };

  const googleLogin = async () => {
    try {
      const data = await apiGoogleLogin();
      if (data.requiresTwoFactor) return { ...data, success: false };
      if (!data.token) {
        throw new Error("Google login did not return an access token");
      }
      await requireCustomerUser(data);
      localStorage.setItem("bookscape_token", data.token);
      setUser(data.user);
      setIsAuthenticated(true);
      window.dispatchEvent(
        new CustomEvent("bookscape:auth-changed", {
          detail: { authenticated: true },
        }),
      );
      await loadUserData();
      return { success: true, message: "Login successful!" };
    } catch (error) {
      return {
        success: false,
        message: error.message || "Google login failed",
      };
    }
  };

  const updateSecuritySession = (data) => {
    if (data.token) localStorage.setItem("bookscape_token", data.token);
    if (data.user) setUser(data.user);
  };

  const verifyTwoFactor = async (challengeId, code) => {
    try {
      const data = await apiVerifyTwoFactor(challengeId, code);
      await requireCustomerUser(data);
      updateSecuritySession(data);
      setIsAuthenticated(true);
      window.dispatchEvent(
        new CustomEvent("bookscape:auth-changed", {
          detail: { authenticated: true },
        }),
      );
      await loadUserData();
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  return {
    verifyTwoFactor,
    updateSecuritySession,
    login,
    signup,
    logout,
    googleLogin,
    resetPassword,
    resendVerification,
    updateProfile,
    changePassword,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    refreshOrders,
    initFromToken,
  };
};
